import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { CmsMenu } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { ContentMenuComponent } from './content-menu.component';

/**
 * Navigation menus list (PAGE-CMS.md "Menus") — one row per header group /
 * footer column. Group ORDER is the nav order on the site, so rows carry
 * ▲▼ (persisted via the upsert's `index` override).
 *
 * Menus have no per-item ☁: they ship with any "Publish preview" and appear
 * in the promote dialog as "Navigation menus" rows.
 */
@Component({
  selector: 'app-content-menu-list',
  templateUrl: './content-menu-list.component.html',
})
export class ContentMenuListComponent implements OnInit, OnDestroy {
  accountLoading = true;
  loadFailed = false;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;
  saving = false;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: (err) => {
        this.accountLoading = false;
        this.loadFailed = true;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  menusAsList(content: any, location: 'header' | 'footer'): CmsMenu[] {
    const data = content?.menus;
    if (!data) return [];
    const list = (Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean)) as CmsMenu[];
    return _.sortBy(list.filter((m) => (m.location === 'footer' ? 'footer' : 'header') === location),
      (m) => m?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  itemCount(menu: CmsMenu): number {
    const raw = menu?.items;
    if (!raw) return 0;
    return (Array.isArray(raw) ? raw : Object.values(raw)).filter(Boolean).length;
  }

  onAdd(location: 'header' | 'footer'): void {
    const seed: CmsMenu = {
      _meta: { id: `id-menu-${location}-${this.shortId()}`, index: 0 },
      name: '',
      location,
      items: {},
    };
    this.openEditor(seed, true);
  }

  onEdit(menu: CmsMenu): void {
    this.openEditor(_.cloneDeep(menu), false);
  }

  onDelete(menu: CmsMenu): void {
    const modal = this.modalService.generic({
      title: 'Remove menu',
      copy: [
        `Remove "${menu.name}" and its links from the site's ${menu.location === 'footer' ? 'footer' : 'menu'}? If every ${menu.location} menu is removed, the site falls back to its built-in navigation.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        this.accountService
          .removeContentItem('menus', menu._meta.id)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }

  /** Swap a group with its neighbour by persisting explicit indexes for both. */
  onMove(menu: CmsMenu, delta: number, content: any): void {
    if (this.saving) return;
    const list = this.menusAsList(content, menu.location === 'footer' ? 'footer' : 'header');
    const i = list.findIndex((m) => m._meta.id === menu._meta.id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;

    const a = list[i];
    const b = list[j];
    const ai = a._meta.index ?? i * 10;
    const bi = b._meta.index ?? j * 10;

    this.saving = true;
    this.persist({ ...this.stripMeta(a), index: bi }, () => {
      this.persist({ ...this.stripMeta(b), index: ai }, () => (this.saving = false));
    });
  }

  private stripMeta(menu: CmsMenu): any {
    const { _meta, ...fields } = menu as any;
    return { id: _meta?.id, ...fields, items: this.itemsAsMap(menu) };
  }

  /** items keyed by _meta.id whatever shape the account stream returned. */
  private itemsAsMap(menu: CmsMenu): Record<string, any> {
    const raw: any = menu?.items;
    if (!raw) return {};
    const list: any[] = Array.isArray(raw) ? raw.filter(Boolean) : Object.values(raw).filter(Boolean);
    const map: Record<string, any> = {};
    list.forEach((it, i) => {
      let id = it?._meta?.id || `id-i${(i + 1) * 10}`;
      while (map[id]) id = `${id}-${i}`;
      map[id] = { ...it, _meta: { id, index: typeof it?._meta?.index === 'number' ? it._meta.index : i * 10 } };
    });
    return map;
  }

  /**
   * NOT takeUntil'd, and take(1) rather than first().
   *
   * takeUntil(unsubscribe$) on a WRITE does two bad things when the modal is
   * closed while the request is in flight: it ABORTS the save, and it completes
   * the stream empty so first() throws EmptyError — which surfaces as a
   * "no elements in sequence" error dialog. A content write must outlive the
   * dialog that started it (same reasoning as recordPublishedSlug in the blog
   * list); take(1) completes on its own, so nothing leaks.
   */
  private persist(item: any, done?: () => void): void {
    this.accountService
      .patchContentItem('menus', item)
      .pipe(take(1))
      .subscribe({
        next: () => done?.(),
        error: (err) => {
          this.saving = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  private openEditor(menu: CmsMenu, isNew: boolean): void {
    const ref = this.dialogService.open(ContentMenuComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'New menu' : `Edit ${menu.name || 'menu'}`,
      width: '46rem',
      baseZIndex: 11000,
      data: { menu, isNew },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; menu: any } | undefined) => {
        if (!result?.saved) return;
        this.persist(result.menu);
      },
    });
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 8);
  }
}
